import {ReactReduxContext, useDispatch, useSelector} from 'react-redux';
import {setCurrentStep, setError, setFormData} from '../../actions';
import FormStep from '.';
import AutoHeader from '../autoHeader';
import {useRef, useEffect, useContext} from "react";
import StartOverButton from '../startOverButton';

import FORM_DATA from "./bi/constants/FORM_DATA";
import {
    RESIDENCY_CALIFORNIA,
    RESIDENCY_NONE,
    RESIDENCY_NULL_CHECK,
    SELECT_NULL_CHECK
} from "./bi/constants/MAGIC_NUMBERS";
import {StepIndicator} from "../stepIndicator";

/**
 * Represents Step 2 component.
 * @constructor
 * @returns {JSX.Element} Step 2 component
 */
const Step2 = () => {
    const dispatch = useDispatch();
    const context = useContext(ReactReduxContext);

    const errors = useSelector((state) => state.app.errors.step2);
    const hasErrors =  Object.keys(errors)
    .filter((field) => errors[field])
    .length > 0;
  

    return (
        <FormStep>
            <AutoHeader text="Step 2" />
            <StepIndicator title="Previous Step" previous={true} prevStep={1}/>

            <div className="section-2 fae-section-content">
            { hasErrors ? <Step2Errors errors={errors}/> : null }
                <div className="alert d-none">
                    <p>Before proceeding to the next step, you need to select:</p>
                    <ul>
                        <li>Campus field</li>
                    </ul>
                </div>
                <p>Please select your desired campus and housing selection (this can be changed later)</p>

                <Step2Select ctx={context} data={FORM_DATA.campusList} field="campus" text="Select Campus" />
                <Step2Select ctx={context} data={FORM_DATA.housingList} field="housing" text="Housing Type" />

                <br/>
                <p>Please indicate if California is your primary and permanent state of residence.</p>
                <p>If you are unsure, please refer to the <a className="fae-link"
                           href="https://www.ucop.edu/uc-legal/_files/ed-affairs/uc-residence-policy.pdf">UC Residency Policy</a>.
                </p>

                <Step2Select ctx={context} data={FORM_DATA.residencyList} field="residency" text="Residency Status" />

                <button
                    type="submit"
                    onClick={(e) => {
                        e.preventDefault();
                        dispatch(Step2NextClickThunk());
                    }}
                >Next
                </button>
            </div>
        </FormStep>
    )
};

const ERROR_TEXT = {
	campus: 'Desired Campus',
	housing: 'Housing Type',
	residency: 'Residency'
};

/**
 * Represents Step 2 error component.
 * {object} errors - error state from store
 * @returns {JSX.Element} Step 2 error component
 */
const Step2Errors = (props) => {
  const errorRef = useRef(null);
  const {errors} = props;

  const errorMessages = Object.keys(errors).reduce((text, field) => {
    const hasError = errors[field];

    if (hasError) {
      text.push(ERROR_TEXT[field]);
    }

    return text;
  }, []);

  useEffect(() => {
    if (errorRef.current) {
      errorRef.current.focus();
    }
  }, [errorRef, errors]);

  return (
    <div className="section-2-error-container">
    <span ref={errorRef} tabIndex="-1" className="section-2-error-text">{"Before proceeding to the next step you'll need to select:"}</span>
    <ul className="section-2-error-list">
    {
      errorMessages.map((message, idx) => (
        <li key={idx} className="section-2-error-item">{message}</li>
      ))
    }
    </ul>
    </div>
  );
};

/**
 * Executes a thunk function that handles the next step click in Step 2.
 * @return {Function} The thunk function that can be dispatched.
 */
const Step2NextClickThunk = () => {
    return function (dispatch, getState) {

        const keys = ['campus', 'housing', 'residency'];

        let startTest = true;

        let v = {};
        keys.forEach((key) => {
            let check = TestStep2DropDown(getState, key);
            v[key] = check;
            startTest &&= !check;
        })
        dispatch(setError(2, v));  
        if(startTest) {
            dispatch(setError(3, {}));
            //  Make the swap here.
            if(getState().app.stepData.step2['residency'] === RESIDENCY_CALIFORNIA)
            {
                dispatch(setCurrentStep(3));
            } else {
                dispatch(setCurrentStep(4));
            }
        }
    }
};

/**
 * Renders a custom select component for Step 2.
 *
 * @param {object} ctx - The context object.
 * @param {array} data - The dropdown data to be displayed.
 * @param {string} field - The field name to identify the dropdown.
 * @param {string} text - The label text for the dropdown.
 *
 * @returns {JSX.Element} - The custom select component.
 */
function Step2Select({ctx, data, field, text}) {
    const source = () => ctx.store.getState().app.stepData.step2[field];
    const map = useSelector(source);

    return(
        <Step2DropDown fieldName={field} labelText={text} dropData={data} val={map}/>
    )
}

/**
 * Checks if the value of a specified field in the step 2 of an application is equal to -1 or 0.
 *
 * @param {function} getState - A function that returns the current state of the application.
 * @param {string} fieldName - The name of the field to check.
 * @returns {boolean} - Returns true if the field value is -1 or 0, otherwise returns false.
 */
function TestStep2DropDown(getState, fieldName) {
    const data = () => getState().app.stepData.step2[fieldName];
    //  Initially -1, but should return an error value on -1/0 after the first click on "Next"
    return  data() === RESIDENCY_NONE || data() === SELECT_NULL_CHECK;
}

/**
 * Renders a dropdown input field with a label and options.
 *
 * @param {string} labelText - The text to display as the label for the dropdown.
 * @param {string} fieldName - The name to assign to the dropdown field.
 * @param {Array} dropData - An array of objects representing the options for the dropdown.
 *                          Each object should have a "value" and "text" property.
 * @param {number} val - The currently selected value of the dropdown.
 * @returns {JSX.Element} - The rendered dropdown component.
 * @constructor
 */
function Step2DropDown({labelText, fieldName, dropData, val}) {
    const dispatch = useDispatch();
    const context = useContext(ReactReduxContext);

    const error = () => context.store.getState().app.errors.step2[fieldName];
    const errMap = useSelector(error);

    let myClass = (errMap && (val <= 0)) ? 'error' : '';

    return (
        <>
            <label className="sr-only" htmlFor={fieldName}>{labelText}</label>
            <select
                aria-required="true"
                className={myClass}
                id={fieldName}
                name={fieldName}
                value={val}
                onChange={(e) => {
                    e.preventDefault();
                    let v = {};
                    v[fieldName] = parseInt(e.target.value);
                    dispatch(setFormData(2, v));
                }}
            >
                {
                    dropData.map((option, index) => (
                        <option key={index} value={option.value}>{option.text}</option>
                    ))
                }
            </select>
        </>
    );

}

export default Step2;
